/**
 * Date and capacity helpers shared by the public event pages.
 *
 * Every event carries a UTC instant plus the IANA zone it is held in. Showing
 * only one of those is the classic event-site bug: an attendee in Denver reads
 * "3:00 PM" on a DC event page and arrives two hours late. So times are
 * rendered in the viewer's own zone, and the event's zone is named whenever the
 * two differ.
 */

export const viewerTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

/**
 * The API serialises the UTC instants as naive strings ("2026-10-14T18:30:00").
 * `new Date()` would read those as the browser's own wall clock, which silently
 * shifts every event by the viewer's offset — so the zone is pinned to UTC here.
 */
export const parseInstant = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const naive = /\d{2}:\d{2}/.test(raw) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const date = new Date(naive ? `${raw.replace(" ", "T")}Z` : raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatIn = (value, zone, options) => {
  const date = parseInstant(value);
  if (!date) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: zone,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }
};

export const formatEventDate = (value) =>
  formatIn(value, viewerTimeZone(), {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export const formatEventTime = (value) =>
  formatIn(value, viewerTimeZone(), { hour: "numeric", minute: "2-digit" });

export const formatShortDate = (value) =>
  formatIn(value, viewerTimeZone(), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/** e.g. "Tuesday, March 4, 2026 · 3:00 PM – 4:30 PM" */
export const formatEventRange = (startsAt, endsAt) => {
  const date = formatEventDate(startsAt);
  const start = formatEventTime(startsAt);
  const end = formatEventTime(endsAt);
  if (!date) return "";
  return end ? `${date} · ${start} – ${end}` : `${date} · ${start}`;
};

/**
 * Names the event's own zone only when it differs from the viewer's, so a local
 * attendee isn't shown redundant timezone noise.
 */
export const timezoneNote = (eventTimezone, startsAt) => {
  if (!eventTimezone) return "";
  const viewer = viewerTimeZone();
  if (!viewer || viewer === eventTimezone) return "";
  const localTime = formatIn(startsAt, eventTimezone, {
    hour: "numeric",
    minute: "2-digit",
  });
  const label = eventTimezone.split("/").pop().replace(/_/g, " ");
  return `${localTime} in ${label}`;
};

export const isPast = (endsAt) => {
  const date = parseInstant(endsAt);
  return date ? date.getTime() < Date.now() : false;
};

export const capacityLabel = (event) => {
  if (!event) return "";
  if (event.full) return event.waitlistEnabled ? "Waitlist open" : "Full";
  if (event.seatsRemaining === null || event.seatsRemaining === undefined)
    return "";
  if (event.seatsRemaining <= 10) return `${event.seatsRemaining} seats left`;
  return "Seats available";
};

export const formatLabel = (format) =>
  ({
    IN_PERSON: "In person",
    VIRTUAL: "Online",
    HYBRID: "In person + online",
  })[format] || format;

export const typeLabel = (eventType) =>
  ({
    WEBINAR: "Webinar",
    WORKSHOP: "Workshop",
    TALK: "Talk",
    SUMMIT: "Summit",
  })[eventType] || eventType;

export const audienceLabel = (audience) =>
  ({
    SENIORS: "Older adults",
    PARENTS: "Parents & families",
    SMB: "Small business",
    FAITH_COMMUNITY: "Faith & community groups",
    GENERAL: "Everyone",
  })[audience] || audience;

/** One-line "where" for summary panels; venue and platform live in JSON columns. */
export const locationLabel = (event) => {
  if (!event) return "";
  const online = event.virtual?.platform
    ? `Online — ${event.virtual.platform}`
    : "Online";
  if (event.format === "VIRTUAL") return online;
  const venue = event.venue?.name || "Venue to be announced";
  return event.format === "HYBRID" ? `${venue} + online` : venue;
};

/**
 * Admins paste links like "google.com/maps?..." without a scheme, which the
 * browser would resolve against /events/. Force https and drop anything that
 * isn't a plain web link (javascript:, data:).
 */
export const externalUrl = (value) => {
  const raw = (value || "").trim();
  if (!raw) return "";
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
    ? raw
    : `https://${raw.replace(/^\/+/, "")}`;
  try {
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : "";
  } catch {
    return "";
  }
};

/**
 * Hand the venue off to whatever maps app the device already owns — Apple Maps
 * on iOS/macOS, the system app chooser via geo: on Android, a web map
 * elsewhere. The admin-entered mapUrl is only used when there's no address to
 * hand over, since a pasted Google link would ignore the device's choice.
 */
export const venueMapUrl = (venue) => {
  const query = (venue?.address || venue?.name || "").trim();
  if (!query) return externalUrl(venue?.mapUrl);
  const q = encodeURIComponent(query);
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent || "";
  if (/iPad|iPhone|iPod|Macintosh/.test(ua))
    return `https://maps.apple.com/?q=${q}`;
  if (/Android/.test(ua)) return `geo:0,0?q=${q}`;
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

/** Google Calendar template URL — the one add-to-calendar path that can't be an .ics download. */
export const googleCalendarUrl = (event) => {
  const start = parseInstant(event?.startsAt);
  if (!start) return "";
  const end = parseInstant(event.endsAt) || start;
  const stamp = (date) => date.toISOString().replace(/[-:]|\.\d{3}/g, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title || "KinShield Event",
    dates: `${stamp(start)}/${stamp(end)}`,
    details: `${event.summary || ""}\n\n${window.location.origin}/events/${event.slug}`,
    location: event.venue?.address || event.venueName || "Online",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
